
import React, { useReducer, useMemo } from 'react';
import { AppStep, AppState, Action } from './types.ts';
import Header from './components/Header.tsx';
import Stepper from './components/Stepper.tsx';
import UploadPage from './pages/UploadPage.tsx';
import ExtractionPage from './pages/ExtractionPage.tsx';
import DesignPage from './pages/DesignPage.tsx';
import GenerationPage from './pages/GenerationPage.tsx';
import CodeViewPage from './pages/CodeViewPage.tsx';
import PreviewPage from './pages/PreviewPage.tsx';

const initialState: AppState = {
  currentStep: AppStep.UPLOAD,
  imageData: null,
  extractionData: null,
  designData: null,
  generatedCode: null,
  isProcessing: false,
  error: null,
};

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_STEP': return { ...state, currentStep: action.payload };
    case 'SET_IMAGE': return { ...state, imageData: action.payload };
    case 'SET_EXTRACTION': return { ...state, extractionData: action.payload };
    case 'SET_DESIGN': return { ...state, designData: action.payload };
    case 'SET_CODE': return { ...state, generatedCode: action.payload };
    case 'SET_PROCESSING': return { ...state, isProcessing: action.payload };
    case 'SET_ERROR': return { ...state, error: action.payload };
    case 'RESET': return initialState;
    default: return state;
  }
}

export const AppContext = React.createContext<{
  state: AppState;
  dispatch: React.Dispatch<Action>;
} | null>(null);

const App: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const currentPage = useMemo(() => {
    switch (state.currentStep) {
      case AppStep.UPLOAD: return <UploadPage />;
      case AppStep.EXTRACTION: return <ExtractionPage />;
      case AppStep.DESIGN: return <DesignPage />;
      case AppStep.GENERATION: return <GenerationPage />;
      case AppStep.CODE_VIEW: return <CodeViewPage />;
      case AppStep.PREVIEW: return <PreviewPage />;
      default: return <UploadPage />;
    }
  }, [state.currentStep]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100 selection:bg-indigo-500 selection:text-white">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
          <Stepper currentStep={state.currentStep} />
          <div className="mt-8 transition-all duration-500 animate-in fade-in slide-in-from-bottom-4">
            {currentPage}
          </div>
        </main>
        
        {state.error && (
          <div className="fixed bottom-8 right-8 bg-red-500/10 border border-red-500 text-red-400 px-6 py-4 rounded-xl shadow-2xl backdrop-blur-md animate-bounce">
            <p className="font-semibold">Error: {state.error}</p>
            <button 
              onClick={() => dispatch({ type: 'SET_ERROR', payload: null })}
              className="mt-2 text-xs underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </AppContext.Provider>
  );
};

export default App;
